/**
 * Названия ресурсов ролевой модели — левой части `resource:action` (DEV-1777).
 *
 * Названия САМИХ прав словаря не требуют: `ListPermissions` отдаёт `description`,
 * и в базе он заполнен по-русски миграциями («Просмотр сделок», «Создание
 * агентов в CRM»). А вот ресурс приходит только машинным словом из имени права,
 * и заголовок группы в конструкторе роли читался как `residential_complexes`.
 *
 * Здесь ровно 23 ключа вместо восьмидесяти: ресурсов на порядок меньше, чем
 * прав, и новое право старого ресурса словаря не требует вовсе.
 *
 * Неизвестный ресурс отдаётся сырым (см. useResourceLabel) — лучше машинное
 * слово, чем пустой заголовок над чекбоксами. Полная локализация справочника
 * на бэкенде — DEV-1285.
 */
type LocaleOverride = Record<string, unknown>

type Locale = 'ru' | 'en' | 'vn'

const build = (l: Locale): LocaleOverride => ({
  admin: {
    resources: {
      agents: { ru: 'Агенты', en: 'Agents', vn: 'Đại lý' }[l],
      api_docs: { ru: 'Документация API', en: 'API docs', vn: 'Tài liệu API' }[l],
      articles: { ru: 'Статьи', en: 'Articles', vn: 'Bài viết' }[l],
      calendar: { ru: 'Календарь', en: 'Calendar', vn: 'Lịch' }[l],
      clients: { ru: 'Клиенты', en: 'Clients', vn: 'Khách hàng' }[l],
      communications: { ru: 'Коммуникации', en: 'Communications', vn: 'Liên lạc' }[l],
      deals: { ru: 'Сделки', en: 'Deals', vn: 'Giao dịch' }[l],
      developers: { ru: 'Застройщики', en: 'Developers', vn: 'Chủ đầu tư' }[l],
      documents: { ru: 'Документы', en: 'Documents', vn: 'Tài liệu' }[l],
      files: { ru: 'Файлы', en: 'Files', vn: 'Tệp' }[l],
      // DEV-2118. Технические сервисы: доступ к ним выдаётся ролью в CRM, а
      // сами они живут вне её — это не разделы интерфейса.
      gitlab: { ru: 'GitLab', en: 'GitLab', vn: 'GitLab' }[l],
      grafana: { ru: 'Grafana', en: 'Grafana', vn: 'Grafana' }[l],
      monitoring: { ru: 'Мониторинг', en: 'Monitoring', vn: 'Giám sát' }[l],
      vault: { ru: 'Vault', en: 'Vault', vn: 'Vault' }[l],
      landings: { ru: 'Лендинги', en: 'Landing pages', vn: 'Trang đích' }[l],
      // DEV-2005: видеовстречи с клиентом. Записи и расшифровки — под отдельным
      // действием read_recordings того же ресурса.
      meetings: { ru: 'Видеовстречи', en: 'Video meetings', vn: 'Cuộc họp video' }[l],
      // DEV-1711: общие аккаунты компании, не личные профили менеджеров
      // (у тех свой ресурс telegram).
      messengers: {
        ru: 'Общие аккаунты мессенджеров',
        en: 'Shared messenger accounts',
        vn: 'Tài khoản nhắn tin chung',
      }[l],
      partner_companies: {
        ru: 'Компании-партнёры',
        en: 'Partner companies',
        vn: 'Công ty đối tác',
      }[l],
      pipeline: { ru: 'Воронка продаж', en: 'Sales pipeline', vn: 'Quy trình bán hàng' }[l],
      properties: { ru: 'Объекты', en: 'Properties', vn: 'Bất động sản' }[l],
      rc_analytics: { ru: 'Аналитика ЖК', en: 'Complex analytics', vn: 'Phân tích dự án' }[l],
      references: { ru: 'Справочники', en: 'Reference data', vn: 'Danh mục tham chiếu' }[l],
      reports: { ru: 'Отчёты', en: 'Reports', vn: 'Báo cáo' }[l],
      residential_complexes: {
        ru: 'Жилые комплексы',
        en: 'Residential complexes',
        vn: 'Dự án nhà ở',
      }[l],
      sections: { ru: 'Разделы приложений', en: 'Application sections', vn: 'Mục ứng dụng' }[l],
      telegram: { ru: 'Telegram-профили', en: 'Telegram profiles', vn: 'Hồ sơ Telegram' }[l],
      telephony: { ru: 'Телефония', en: 'Telephony', vn: 'Tổng đài' }[l],
      users: { ru: 'Пользователи и доступы', en: 'Users and access', vn: 'Người dùng và truy cập' }[
        l
      ],
    },
  },
})

export const adminResourcesMessages = {
  ru: build('ru'),
  en: build('en'),
  vn: build('vn'),
}
